// ─────────────────────────────────────────────────────────────────────────────
// Declarative report definitions for every non-grievance report, rendered by
// ReportEngine against the mock domains in data/mockClinical.js.
//
// Each entry: { domain, subtitle, build(rows, ctx) -> { kpis, panels } }
// Keep each def small; share the helpers below.
// ─────────────────────────────────────────────────────────────────────────────

import React from "react";
import { average, median } from "./grievanceModel";
import { fmtDate, fmtNum, Pill } from "./reports/_shared";

const PALETTE = ["#0f4c81", "#0891b2", "#059669", "#7c3aed", "#be123c", "#d97706", "#0369a1", "#475569"];

// column helper
const col = (label, get, opts = {}) => ({ label, get, ...opts });

// Granular PCC "Type/Nature of Incident" (Bruise, Skin tear, …), falling back to
// the coarse bucket when the live feed hasn't supplied a nature. Used as both a
// table column and a grouping key so incident reports mirror PCC's own list.
const natureLabel = (r) => r.nature || (r.type ? r.type.charAt(0).toUpperCase() + r.type.slice(1) : "—");
const natureCol = col("Type/Nature", (r) => natureLabel(r));

// group → bar data
function groupBars(rows, keyFn, valFn, agg = "sum", color) {
  const m = {};
  rows.forEach((r) => {
    const k = keyFn(r);
    if (k == null || k === "") return;
    (m[k] = m[k] || []).push(valFn ? valFn(r) : 1);
  });
  return Object.entries(m)
    .map(([label, vals], i) => ({
      label,
      value: agg === "avg" ? +(vals.reduce((a, b) => a + b, 0) / vals.length).toFixed(1) : vals.reduce((a, b) => a + b, 0),
      color: color || PALETTE[i % PALETTE.length],
    }))
    .sort((a, b) => b.value - a.value);
}

// last-6-month trend from an ISO date field
function monthBars(rows, dateFn, color = "#0891b2") {
  const now = new Date("2026-06-17");
  const months = [];
  for (let i = 5; i >= 0; i--) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    months.push({ key: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`, label: d.toLocaleDateString("en-US", { month: "short" }), value: 0, color });
  }
  const idx = Object.fromEntries(months.map((m, i) => [m.key, i]));
  rows.forEach((r) => {
    const iso = dateFn(r);
    if (!iso) return;
    const key = String(iso).slice(0, 7);
    if (key in idx) months[idx[key]].value += 1;
  });
  return months;
}

const pct = (n) => (n == null ? "—" : `${fmtNum(n, 1)}%`);
const rate = (num, den) => (den ? Math.round((num / den) * 100) : null);

// whole days between an ISO date and today (positive = in the past).
const DAY_MS = 86400000;
const daysSince = (iso) => (iso ? Math.floor((Date.now() - new Date(iso).getTime()) / DAY_MS) : null);

// Classify an assessment / UDA `type` into a survey-risk bucket. These line up
// with the Mock Survey engine's deterministic F-tag triggers (Fall→F689,
// Skin→F686, Nutrition→F692, Pain→F697) so the Metrics rollup and the survey
// flags reference the same risk screens.
function riskType(type) {
  const t = String(type || "").toLowerCase();
  if (/fall/.test(t)) return "Fall Risk";
  if (/pressure|skin|ulcer|wound/.test(t)) return "Pressure Ulcer / Skin";
  if (/wander|elope/.test(t)) return "Wandering";
  if (/nutri|weight|dietary|nutrition/.test(t)) return "Nutrition";
  if (/pain/.test(t)) return "Pain";
  return "Other (MDS)";
}

const statusPill = (s) => {
  const map = {
    Completed: ["#166534", "#f0fdf4"], Success: ["#166534", "#f0fdf4"], Active: ["#166534", "#f0fdf4"], Live: ["#166534", "#f0fdf4"],
    Due: ["#92400e", "#fffbeb"], Warning: ["#92400e", "#fffbeb"], Partial: ["#92400e", "#fffbeb"], Trial: ["#92400e", "#fffbeb"],
    Overdue: ["#991b1b", "#fef2f2"], Failed: ["#991b1b", "#fef2f2"], Denied: ["#991b1b", "#fef2f2"], Disabled: ["#991b1b", "#fef2f2"],
  };
  const [color, bg] = map[s] || ["#334155", "#f1f5f9"];
  return <Pill label={s} color={color} bg={bg} />;
};

const riskPill = (s) => {
  const map = { Low: ["#166534", "#f0fdf4"], Moderate: ["#92400e", "#fffbeb"], High: ["#991b1b", "#fef2f2"] };
  const [color, bg] = map[s] || ["#334155", "#f1f5f9"];
  return <Pill label={s} color={color} bg={bg} />;
};

const yesNo = (b) => (b ? <span style={{ color: "#059669", fontWeight: 700 }}>Yes</span> : <span style={{ color: "#dc2626", fontWeight: 700 }}>No</span>);
const facilityCol = col("Facility", (r) => r.facilityName);

// ─────────────────────────────────────────────────────────────────────────────
export const REPORT_DEFS = {
  // ── COVID-19 ────────────────────────────────────────────────────────────────
  CovidVaccination: {
    // LIVE PCC: immunizations domain (per-resident immunization records). PCC has
    // no staff-vaccination or monthly-rollup data, so this reports resident
    // vaccination coverage from the actual records rather than the old
    // staff/month shape.
    domain: "immunizations",
    emptyHint: "Needs immunization records in PointClickCare for this facility's residents. The report is live — it fills as soon as PointClickCare returns vaccination records in range.",
    subtitle: "Resident vaccination records from PointClickCare — COVID-19 and influenza coverage by facility.",
    build: (rows) => {
      const isCovid = (r) => r.category === "COVID-19";
      const isFlu = (r) => r.category === "Influenza";
      const administered = (r) => r.administered;
      const residentsWithCovid = new Set(rows.filter((r) => isCovid(r) && administered(r)).map((r) => r.residentId || r.residentName));
      const residentsWithFlu = new Set(rows.filter((r) => isFlu(r) && administered(r)).map((r) => r.residentId || r.residentName));
      // Per-facility COVID coverage = distinct residents with a COVID dose / distinct
      // residents that have any immunization record for that facility.
      const facMap = {};
      rows.forEach((r) => {
        const f = r.facilityName || "—";
        (facMap[f] = facMap[f] || { res: new Set(), covid: new Set() }).res.add(r.residentId || r.residentName);
        if (isCovid(r) && administered(r)) facMap[f].covid.add(r.residentId || r.residentName);
      });
      const facBars = Object.entries(facMap)
        .map(([label, v], i) => ({ label, value: rate(v.covid.size, v.res.size) ?? 0, color: PALETTE[i % PALETTE.length] }))
        .sort((a, b) => b.value - a.value);
      return {
        kpis: [
          { label: "Residents w/ COVID-19", value: residentsWithCovid.size, tone: "green" },
          { label: "Residents w/ Influenza", value: residentsWithFlu.size, tone: "yellow" },
          { label: "Immunization Records", value: rows.length },
        ],
        panels: [
          { type: "bars", title: "COVID-19 Coverage % by Facility (residents with a dose)", data: facBars, max: 100, valueFmt: (v) => `${v}%` },
          { type: "bars", title: "Doses by Vaccine Type", data: groupBars(rows.filter(administered), (r) => r.category) },
          { type: "table", title: "Immunization Detail", rows: [...rows].sort((a, b) => String(b.date || "").localeCompare(String(a.date || ""))), columns: [
            col("Resident", (r) => r.residentName, { strong: true }), facilityCol, col("Vaccine", (r) => r.immunization || r.category), col("Type", (r) => r.category),
            col("Administered", (r) => yesNo(r.administered)), col("Date", (r) => fmtDate(r.date)), col("Dose", (r) => r.doseNumber || "—"), col("Manufacturer", (r) => r.manufacturer || "—"),
          ] },
        ],
      };
    },
  },
  CovidCaseLog: {
    domain: "covid",
    subtitle: "Resident and staff case counts over the last 6 months.",
    build: (rows) => ({
      kpis: [
        { label: "Resident Cases", value: rows.reduce((a, r) => a + r.residentCases, 0), tone: "red" },
        { label: "Staff Cases", value: rows.reduce((a, r) => a + r.staffCases, 0), tone: "yellow" },
      ],
      panels: [
        { type: "bars", title: "Resident Cases by Facility", data: groupBars(rows, (r) => r.facilityName, (r) => r.residentCases, "sum", "#be123c") },
        { type: "table", title: "Case Log", rows, columns: [facilityCol, col("Month", (r) => r.month), col("Resident", (r) => r.residentCases, { align: "right", strong: true }), col("Staff", (r) => r.staffCases, { align: "right" })] },
      ],
    }),
  },
  CovidTesting: {
    domain: "covid",
    subtitle: "Testing volume across the portfolio.",
    build: (rows) => ({
      kpis: [{ label: "Total Tests", value: rows.reduce((a, r) => a + r.tests, 0) }, { label: "Facilities", value: new Set(rows.map((r) => r.facilityCcn)).size }],
      panels: [
        { type: "bars", title: "Tests by Facility", data: groupBars(rows, (r) => r.facilityName, (r) => r.tests) },
        { type: "table", title: "Detail", rows, columns: [facilityCol, col("Month", (r) => r.month), col("Tests", (r) => r.tests, { align: "right", strong: true })] },
      ],
    }),
  },

  // ── Quality Measures ──────────────────────────────────────────────────────
  QMLongStay: {
    domain: "qualityMeasures",
    subtitle: "Long-stay quality measures by facility (% of residents).",
    build: (rows) => ({
      kpis: [
        { label: "Avg Antipsychotics", value: pct(average(rows.map((r) => r.antipsychotics))), tone: "yellow" },
        { label: "Avg Falls w/ Injury", value: pct(average(rows.map((r) => r.fallsWithInjury))), tone: "red" },
        { label: "Avg Pressure Ulcers", value: pct(average(rows.map((r) => r.pressureUlcers))), tone: "yellow" },
      ],
      panels: [
        { type: "bars", title: "Antipsychotic Use by Facility", data: groupBars(rows, (r) => r.facilityName, (r) => r.antipsychotics, "avg", "#7c3aed"), valueFmt: (v) => `${v}%` },
        { type: "table", title: "Measures", rows, columns: [facilityCol, col("Falls w/Inj", (r) => pct(r.fallsWithInjury), { align: "right" }), col("Press. Ulcers", (r) => pct(r.pressureUlcers), { align: "right" }), col("Antipsych", (r) => pct(r.antipsychotics), { align: "right" }), col("UTIs", (r) => pct(r.utis), { align: "right" }), col("Wt Loss", (r) => pct(r.weightLoss), { align: "right" })] },
      ],
    }),
  },
  QMShortStay: {
    domain: "qualityMeasuresShort",
    subtitle: "Short-stay quality measures by facility (% of residents).",
    build: (rows) => ({
      kpis: [
        { label: "Avg Improved Function", value: pct(average(rows.map((r) => r.improvedFunction))), tone: "green" },
        { label: "Avg Rehospitalized", value: pct(average(rows.map((r) => r.rehospitalized))), tone: "red" },
        { label: "Avg Community Discharge", value: pct(average(rows.map((r) => r.communityDischarge))), tone: "green" },
      ],
      panels: [
        { type: "bars", title: "Rehospitalization by Facility", data: groupBars(rows, (r) => r.facilityName, (r) => r.rehospitalized, "avg", "#be123c"), valueFmt: (v) => `${v}%` },
        { type: "table", title: "Measures", rows, columns: [facilityCol, col("Improved Fn", (r) => pct(r.improvedFunction), { align: "right" }), col("Rehosp", (r) => pct(r.rehospitalized), { align: "right" }), col("ED Visits", (r) => pct(r.edVisits), { align: "right" }), col("Community DC", (r) => pct(r.communityDischarge), { align: "right" })] },
      ],
    }),
  },
  QMComparison: {
    domain: "qualityMeasures",
    subtitle: "Side-by-side long-stay hospitalization across facilities.",
    build: (rows) => ({
      kpis: [{ label: "Facilities", value: rows.length }, { label: "Avg Long-Stay Hosp", value: pct(average(rows.map((r) => r.longStayHosp))), tone: "yellow" }],
      panels: [
        { type: "bars", title: "Long-Stay Hospitalization by Facility", data: groupBars(rows, (r) => r.facilityName, (r) => r.longStayHosp, "avg"), valueFmt: (v) => `${v}%` },
        { type: "table", title: "Comparison", rows, columns: [facilityCol, col("Long-Stay Hosp", (r) => pct(r.longStayHosp), { align: "right", strong: true }), col("Falls w/Inj", (r) => pct(r.fallsWithInjury), { align: "right" }), col("Antipsych", (r) => pct(r.antipsychotics), { align: "right" })] },
      ],
    }),
  },

  // ── Clinical ──────────────────────────────────────────────────────────────
  AdvanceDirectiveCompliance: {
    domain: "residents",
    subtitle: "Advance directive on file — F578 readiness.",
    build: (rows) => {
      const onFile = rows.filter((r) => r.advanceDirective);
      // In PointClickCare a resident's code status (DNR / Full Code / POLST) is
      // stored as an ORDER, not a field on the patient record — and the Orders
      // API is a separate PCC "product" the integration must be entitled to.
      // Until that scope is granted, every resident reads as "missing" even
      // when their chart shows a DNR. Explain that rather than imply 0% real.
      const scopeGap = rows.length > 0 && onFile.length === 0;
      const panels = [];
      if (scopeGap) {
        panels.push({
          type: "note",
          title: "Why is this 0%?",
          // ── THE DATE, BECAUSE EVERY OTHER BLOCKED SCOPE IN THIS PRODUCT CARRIES ONE ──
          //
          // The mechanism here was already right: a directive is derived from the resident's
          // ORDERS (pccIntegration.js — directiveFromOrders(fetchPatientOrders(...))), and
          // PCC_ORDERS_ENABLED really is the switch. What was wrong is that it said "not yet
          // granted" with no date, while the worksheets and the chart check were changed to
          // name November 2026 wherever a scope has a date. An undated "not yet" reads as
          // indefinite, and a reader who has just seen three dated rows elsewhere concludes
          // this one is never coming.
          //
          // November 2026 is PENDING_SCOPES.ALL_ORDERS (confirmed with Annan, 4 Oct 2026):
          // general, treatment, diet and therapy orders land with care plans. An advance
          // directive is a general order, so it arrives in that grant.
          text:
            "Advance directives (code status) are read from each resident's orders in PointClickCare, not from the resident record — and only medication orders are readable today. The rest of the order book arrives in November 2026, alongside care plans, so this report starts populating then. Until it does, 0% means we cannot see directives, NOT that nobody has one: residents may well have a DNR on file. This is a PointClickCare access grant, not a data-entry gap, and nothing needs to be entered differently. The derivation is already written and switches on with PCC_ORDERS_ENABLED=true the day the grant lands.",
        });
      }
      return {
        kpis: [
          { label: "Residents", value: rows.length },
          { label: "Directive on File", value: scopeGap ? "—" : `${rate(onFile.length, rows.length) ?? "—"}%`, tone: scopeGap ? undefined : rate(onFile.length, rows.length) >= 90 ? "green" : "yellow" },
          // THE TILE IS WHAT GETS READ, so the date goes here and not only in the note.
          // "Orders API" told a reader which scope was missing and nothing about whether to
          // wait for it; "Nov 2026" answers the question they actually have.
          { label: scopeGap ? "Readable from" : "Missing", value: scopeGap ? "Nov 2026" : rows.length - onFile.length, tone: scopeGap ? "yellow" : rows.length - onFile.length === 0 ? "green" : "red" },
        ],
        panels: [
          ...panels,
          { type: "bars", title: "By Directive", data: groupBars(rows, (r) => r.advanceDirective || "None on file") },
          { type: "table", title: "Missing Advance Directive", rows: rows.filter((r) => !r.advanceDirective), empty: "All residents have a directive on file.", columns: [col("Resident", (r) => r.name, { strong: true }), facilityCol, col("Level", (r) => r.levelOfCare), col("Room", (r) => r.room), col("Admitted", (r) => fmtDate(r.admitDate))] },
        ],
      };
    },
  },
  AMADischarges: {
    domain: "transfers",
    subtitle: "Discharges against medical advice.",
    build: (rows) => {
      const ama = rows.filter((r) => r.kind === "AMA");
      return {
        kpis: [{ label: "AMA Discharges", value: ama.length, tone: ama.length ? "red" : "green" }],
        panels: [{ type: "table", title: "AMA Discharges", rows: ama, empty: "No AMA discharges in scope.", columns: [col("Date", (r) => fmtDate(r.date)), col("Resident", (r) => r.residentName, { strong: true }), facilityCol, col("Reason", (r) => r.reason, { wrap: true }), col("Returned", (r) => yesNo(r.returned), { align: "center" })] }],
      };
    },
  },
  AssessmentResponseReview: {
    domain: "assessments",
    subtitle: "MDS assessment completion and timeliness.",
    build: (rows) => {
      const completed = rows.filter((r) => r.status === "Completed");
      const overdue = rows.filter((r) => r.status === "Overdue");
      return {
        kpis: [
          { label: "Assessments", value: rows.length },
          { label: "Completed", value: `${rate(completed.length, rows.length) ?? "—"}%`, tone: "green" },
          { label: "Overdue", value: overdue.length, tone: overdue.length ? "red" : "green" },
        ],
        panels: [
          { type: "bars", title: "By Status", data: groupBars(rows, (r) => r.status) },
          { type: "bars", title: "By Type", data: groupBars(rows, (r) => r.type) },
        ],
      };
    },
  },
  AssessmentsDue: {
    domain: "assessments",
    subtitle: "Due & overdue assessments — most overdue first, grouped by survey-risk type.",
    build: (rows) => {
      const open = rows.filter((r) => r.status !== "Completed");
      const overdue = open
        .filter((r) => r.status === "Overdue" || (r.dueDate && (daysSince(r.dueDate) ?? 0) > 0))
        .map((r) => ({ ...r, overdueDays: Math.max(0, daysSince(r.dueDate) ?? 0) }))
        .sort((a, b) => b.overdueDays - a.overdueDays);
      return {
        kpis: [
          { label: "Due / Overdue", value: open.length, tone: open.length ? "yellow" : "green" },
          { label: "Overdue", value: overdue.length, tone: overdue.length ? "red" : "green" },
          { label: "Overdue Rate", value: `${rate(overdue.length, rows.length) ?? 0}%`, tone: "yellow" },
        ],
        panels: [
          { type: "bars", title: "Overdue by Survey-Risk Type", data: groupBars(overdue, (r) => riskType(r.type)) },
          { type: "table", title: "Most Overdue", rows: overdue.slice(0, 40), empty: "Nothing overdue in scope.", columns: [col("Overdue", (r) => `${r.overdueDays}d`, { align: "right", strong: true }), col("Due", (r) => fmtDate(r.dueDate)), col("Resident", (r) => r.residentName, { strong: true }), facilityCol, col("Type", (r) => r.type), col("Risk", (r) => riskType(r.type)), col("Status", (r) => statusPill(r.status), { align: "center" })] },
        ],
      };
    },
  },
  AvailableBeds: {
    domain: "census",
    subtitle: "Capacity, occupancy, and available beds by facility.",
    build: (rows) => ({
      kpis: [
        { label: "Available Beds", value: rows.reduce((a, r) => a + r.available, 0), tone: "neutral" },
        { label: "Avg Occupancy", value: pct(average(rows.map((r) => r.occupancyPct))), tone: "green" },
        { label: "Total Capacity", value: rows.reduce((a, r) => a + r.capacity, 0) },
      ],
      panels: [
        { type: "bars", title: "Available Beds by Facility", data: groupBars(rows, (r) => r.facilityName, (r) => r.available) },
        { type: "table", title: "Census", rows, columns: [facilityCol, col("Capacity", (r) => r.capacity, { align: "right" }), col("Occupied", (r) => r.occupied, { align: "right" }), col("Available", (r) => r.available, { align: "right", strong: true }), col("Occupancy", (r) => pct(r.occupancyPct), { align: "right" })] },
      ],
    }),
  },
  AvgMedications: {
    domain: "residents",
    subtitle: "Average medications per resident — polypharmacy signal.",
    build: (rows) => ({
      kpis: [
        { label: "Avg Meds / Resident", value: fmtNum(average(rows.map((r) => r.medsCount)), 1), tone: "yellow" },
        { label: "On 15+ Meds", value: rows.filter((r) => r.medsCount >= 15).length, tone: "red" },
      ],
      panels: [
        { type: "bars", title: "Avg Medications by Facility", data: groupBars(rows, (r) => r.facilityName, (r) => r.medsCount, "avg") },
        { type: "table", title: "Highest Medication Counts", rows: [...rows].sort((a, b) => b.medsCount - a.medsCount).slice(0, 15), columns: [col("Resident", (r) => r.name, { strong: true }), facilityCol, col("Meds", (r) => r.medsCount, { align: "right", strong: true }), col("Level", (r) => r.levelOfCare)] },
      ],
    }),
  },
  ClinicalReport: {
    domain: "residents",
    subtitle: "Census roster across the portfolio.",
    build: (rows) => ({
      kpis: [
        { label: "Residents", value: rows.length },
        { label: "SNF", value: rows.filter((r) => r.levelOfCare === "SNF").length },
        { label: "AL / IL", value: rows.filter((r) => r.levelOfCare !== "SNF").length },
      ],
      panels: [
        { type: "bars", title: "By Payer", data: groupBars(rows, (r) => r.payer) },
        { type: "table", title: "Residents", rows: rows.slice(0, 60), columns: [col("Resident", (r) => r.name, { strong: true }), facilityCol, col("Level", (r) => r.levelOfCare), col("Payer", (r) => r.payer), col("Room", (r) => r.room), col("Attending", (r) => r.attendingMd)] },
      ],
    }),
  },
  ClinicalReportALIL: {
    domain: "residents",
    subtitle: "Assisted Living and Independent Living residents only.",
    build: (rows) => {
      const alil = rows.filter((r) => r.levelOfCare !== "SNF");
      return {
        kpis: [{ label: "AL Residents", value: alil.filter((r) => r.levelOfCare === "AL").length }, { label: "IL Residents", value: alil.filter((r) => r.levelOfCare === "IL").length }],
        panels: [{ type: "table", title: "AL / IL Residents", rows: alil, empty: "No AL/IL residents in scope.", columns: [col("Resident", (r) => r.name, { strong: true }), facilityCol, col("Level", (r) => r.levelOfCare), col("Payer", (r) => r.payer), col("Admitted", (r) => fmtDate(r.admitDate))] }],
      };
    },
  },
  ClinicalReportDetail: {
    domain: "residents",
    subtitle: "Full resident detail with stay and clinical attributes.",
    build: (rows) => ({
      kpis: [{ label: "Residents", value: rows.length }, { label: "Avg LOS (days)", value: fmtNum(average(rows.map((r) => r.losDays)), 0) }],
      panels: [{ type: "table", title: "Resident Detail", rows: rows.slice(0, 60), columns: [col("Resident", (r) => r.name, { strong: true }), facilityCol, col("Level", (r) => r.levelOfCare), col("Payer", (r) => r.payer), col("LOS", (r) => `${r.losDays}d`, { align: "right" }), col("Meds", (r) => r.medsCount, { align: "right" }), col("Directive", (r) => r.advanceDirective || "—"), col("Risk", (r) => riskPill(r.rehospRisk), { align: "center" })] }],
    }),
  },
  FacilityAssessmentData: {
    domain: "census",
    subtitle: "Facility-level census and level-of-care mix for the facility assessment.",
    build: (rows) => ({
      kpis: [{ label: "Facilities", value: rows.length }, { label: "Total Residents", value: rows.reduce((a, r) => a + r.occupied, 0) }],
      panels: [{ type: "table", title: "Facility Assessment Data", rows, columns: [facilityCol, col("CCN", (r) => r.facilityCcn), col("Capacity", (r) => r.capacity, { align: "right" }), col("SNF", (r) => r.snf, { align: "right" }), col("AL", (r) => r.al, { align: "right" }), col("IL", (r) => r.il, { align: "right" }), col("Occupancy", (r) => pct(r.occupancyPct), { align: "right" })] }],
    }),
  },
  ElopementAudit: {
    domain: "incidents",
    subtitle: "Elopement and exit-seeking incidents.",
    build: (rows) => {
      const e = rows.filter((r) => r.type === "elopement");
      return {
        kpis: [{ label: "Elopement Incidents", value: e.length, tone: e.length ? "red" : "green" }],
        panels: [{ type: "table", title: "Elopement Audit", rows: e, empty: "No elopement incidents in scope.", columns: [col("Date", (r) => fmtDate(r.date)), col("Resident", (r) => r.residentName, { strong: true }), facilityCol, col("Location", (r) => r.location), col("Care Plan Updated", (r) => yesNo(r.carePlanUpdated), { align: "center" })] }],
      };
    },
  },
  EndOfDay: {
    domain: "census",
    subtitle: "End-of-day census snapshot.",
    build: (rows) => ({
      kpis: [{ label: "Residents (EOD)", value: rows.reduce((a, r) => a + r.occupied, 0) }, { label: "Available", value: rows.reduce((a, r) => a + r.available, 0) }, { label: "Avg Occupancy", value: pct(average(rows.map((r) => r.occupancyPct))), tone: "green" }],
      panels: [{ type: "table", title: "End of Day by Facility", rows, columns: [facilityCol, col("Occupied", (r) => r.occupied, { align: "right", strong: true }), col("Available", (r) => r.available, { align: "right" }), col("Occupancy", (r) => pct(r.occupancyPct), { align: "right" }), col("Last Load", (r) => r.lastLoad)] }],
    }),
  },
  GGFunctionOutcomes: {
    domain: "ggFunction",
    emptyHint: "Needs locked MDS assessments carrying Section GG items (GG0130 self-care / GG0170 mobility) for this facility's residents. This is sparse in the PointClickCare sandbox — it populates on facilities with completed 5-day and Part A PPS Discharge assessments.",
    emptyDiagnostic: true, // when empty on the live feed, show what the GG scan actually saw

    subtitle: "Observed Section GG functional change (admission → current) from locked MDS — observed change, not the risk-adjusted CMS quality measure.",
    build: (rows) => {
      const distinctResidents = new Set(rows.map((r) => r.residentId || r.residentName)).size;
      const changed = rows.filter((r) => typeof r.change === "number");
      const pendingResidents = new Set(rows.filter((r) => /pending/i.test(String(r.status))).map((r) => r.residentId || r.residentName)).size;
      // GG scale is 06 (independent) → 01 (dependent), so a POSITIVE change = the
      // resident became more independent (improvement).
      return {
        kpis: [
          { label: "Residents", value: distinctResidents },
          { label: "Avg Observed Change", value: fmtNum(average(changed.map((r) => r.change)), 1), tone: "green" },
          { label: "Outcome Pending", value: pendingResidents, tone: pendingResidents ? "yellow" : "green" },
        ],
        panels: [
          { type: "note", title: "How to read this", text: "Section GG scores run 06 (independent) → 01 (dependent), so a positive change means the resident gained independence. Values are read straight from LOCKED MDS assessments (admission 5-day vs. the most recent assessment — the Part A PPS Discharge when present). Residents still in-house show their admission score with the outcome Pending; no change is fabricated. This is OBSERVED change — it is NOT the risk-adjusted CMS SNF-QRP quality measure (that adjusts for admission function, age and comorbidities, which we do not reproduce)." },
          { type: "bars", title: "Avg Observed Functional Change by Facility", data: groupBars(changed, (r) => r.facilityName, (r) => r.change, "avg", "#059669") },
          { type: "table", title: "GG Outcomes", rows: [...rows].sort((a, b) => (a.change ?? 99) - (b.change ?? 99)).slice(0, 60), columns: [col("Resident", (r) => r.residentName, { strong: true }), facilityCol, col("GG Item", (r) => r.domain), col("Admit", (r) => (r.admitScore ?? "—"), { align: "right" }), col("Current", (r) => (r.currentScore ?? "—"), { align: "right" }), col("Goal", (r) => (r.goalScore ?? "—"), { align: "right" }), col("Change", (r) => (r.change == null ? "—" : (r.change > 0 ? `+${r.change}` : r.change)), { align: "right", strong: true }), col("Status", (r) => r.status || "—")] },
        ],
      };
    },
  },
  AdlDocumentation: {
    domain: "adl",
    // WAS: "Add the ADL event group to the subscription's eventGroupList."
    // There is no ADL event group to add. Sending someone to look for one makes
    // us look like we do not know the API we are integrating with — and we do:
    // the absence is documented in pccIntegration.js from technical validation.
    emptyHint: "Shows CNA point-of-care ADL charting — self-performance per activity, shift by shift. It needs the Activities-of-Daily-Living API, which is not yet in scope. PointClickCare publishes no webhook group for ADLs, so this one domain is filled by a once-daily sync rather than events. ADL answers recorded on an assessment are unaffected and already reach the Section GG cards.",
    subtitle: "CNA-charted ADL self-performance per resident (MDS Section G scale: 0 independent → 4 total dependence) — the functional-decline and F676/F677 survey signal.",
    build: (rows) => {
      const scored = rows.filter((r) => r.scored !== false && typeof r.dependenceLevel === "number");
      const residentIds = new Set(scored.map((r) => r.residentId || r.residentName));
      const needAssist = scored.filter((r) => r.dependenceLevel >= 2);
      const totalDep = scored.filter((r) => r.dependenceLevel >= 4);
      const assistResidents = new Set(needAssist.map((r) => r.residentId || r.residentName));
      return {
        kpis: [
          { label: "Residents Charted", value: residentIds.size },
          { label: "Need Assist (≥Limited)", value: `${rate(assistResidents.size, residentIds.size) ?? 0}%`, tone: "yellow" },
          { label: "Total-Dependence Obs.", value: totalDep.length, tone: totalDep.length ? "red" : "green" },
          { label: "Avg Dependence", value: fmtNum(average(scored.map((r) => r.dependenceLevel)), 1) },
        ],
        panels: [
          { type: "note", title: "How to read this", text: "Each row is a CNA-charted ADL observation. Dependence runs 0 (independent) → 4 (total dependence); an observation of Extensive (3) or Total (4) assistance is F677 (ADL care for the dependent resident) exposure, and a worsening trend across observations is F676 (ADLs — decline / maintain) exposure. This is charted documentation from the ADL webhook — it is NOT the risk-adjusted CMS quality measure." },
          { type: "bars", title: "Observations by Self-Performance", data: groupBars(scored, (r) => r.selfPerformance || "Not scored") },
          { type: "bars", title: "Avg Dependence by Activity", data: groupBars(scored, (r) => r.activity, (r) => r.dependenceLevel, "avg", "#7c3aed") },
          { type: "table", title: "ADL Documentation", rows: [...scored].sort((a, b) => (b.dependenceLevel - a.dependenceLevel) || String(b.date).localeCompare(String(a.date))).slice(0, 80), columns: [col("Resident", (r) => r.residentName, { strong: true }), facilityCol, col("Activity", (r) => r.activity), col("Self-Performance", (r) => r.selfPerformance || "—"), col("Support", (r) => r.support || "—"), col("Level", (r) => (r.dependenceLevel ?? "—"), { align: "right", strong: true }), col("Date", (r) => fmtDate(r.date))] },
        ],
      };
    },
  },
  ResidentDiagnoses: {
    domain: "conditions",
    emptyHint: "Populates live from PointClickCare's conditions (resident problem list) — one row per coded diagnosis. Empty means no conditions are on file for this facility's residents yet.",
    subtitle: "Live resident problem list from PointClickCare — every coded diagnosis (ICD-10), its clinical category, principal/secondary rank, and onset. This is the source the CMI import seeds a primary diagnosis from and the risk/survey engine reads for clinical signals.",
    build: (rows) => {
      const coded = rows.filter((r) => r.coded !== false);
      const residents = new Set(coded.map((r) => r.residentId || r.residentName));
      const codes = new Set(coded.map((r) => r.code).filter(Boolean));
      const infections = coded.filter((r) => r.isInfection);
      const infResidents = new Set(infections.map((r) => r.residentId || r.residentName));
      return {
        kpis: [
          { label: "Residents with Dx", value: residents.size },
          { label: "Total Diagnoses", value: coded.length },
          { label: "Distinct ICD-10", value: codes.size },
          { label: "Residents w/ Infection Dx", value: infResidents.size, tone: infResidents.size ? "yellow" : "green" },
        ],
        panels: [
          { type: "note", title: "How to read this", text: "Each row is a coded diagnosis from the resident's PointClickCare problem list. 'Rank' marks principal vs secondary; 'Category' groups the clinical area (the same buckets the PDPM/NTA and infection signals use). This is the live problem list — the source the CMI import seeds a primary diagnosis from, and the survey/risk engine reads for clinical signals." },
          { type: "bars", title: "Diagnoses by Category", data: groupBars(coded, (r) => r.category || "Other") },
          { type: "bars", title: "Most Common Diagnosis Codes", data: groupBars(coded, (r) => r.code || "—") },
          { type: "table", title: "Resident Problem List", rows: [...coded].sort((a, b) => String(a.residentName).localeCompare(String(b.residentName)) || String(a.rank).localeCompare(String(b.rank))).slice(0, 200), columns: [col("Resident", (r) => r.residentName, { strong: true }), facilityCol, col("ICD-10", (r) => r.code || "—", { strong: true }), col("Description", (r) => r.description || "—", { wrap: true }), col("Category", (r) => r.category || "—"), col("Rank", (r) => r.rank || "—"), col("Status", (r) => r.status || "—"), col("Onset", (r) => fmtDate(r.onsetDate))] },
        ],
      };
    },
  },
  EpisodesOfCare: {
    domain: "episodes",
    emptyHint: "Populates live from PointClickCare's Episodes of Care — one row per stay (a continuous care period), split by payer. Empty means no episodes are on file for this facility's residents yet.",
    subtitle: "Stay-level care periods from PointClickCare — each resident's admissions and discharges by payer. The Medicare A episode is the Part A benefit window; a discharge followed by a new episode is a readmission. Complements ADT (the movement events) with the stay boundaries.",
    build: (rows) => {
      const active = rows.filter((r) => r.active);
      const activePartA = active.filter((r) => r.isMedicareA);
      const discharged30 = rows.filter((r) => r.dischargeDate && daysSince(r.dischargeDate) != null && daysSince(r.dischargeDate) <= 30);
      // Readmission: a resident with a discharged episode followed by a later admission.
      const byRes = new Map();
      for (const r of rows) {
        const k = r.residentId || r.residentName;
        if (!k) continue;
        if (!byRes.has(k)) byRes.set(k, []);
        byRes.get(k).push(r);
      }
      let readmitResidents = 0;
      for (const eps of byRes.values()) {
        const sorted = [...eps].sort((a, b) => String(a.admissionDate || "").localeCompare(String(b.admissionDate || "")));
        let sawDischarge = false;
        let readmit = false;
        for (const e of sorted) {
          if (sawDischarge && e.admissionDate) { readmit = true; break; }
          if (e.dischargeDate) sawDischarge = true;
        }
        if (readmit) readmitResidents += 1;
      }
      return {
        kpis: [
          { label: "Active Stays", value: active.length },
          { label: "Active Part A (Medicare A)", value: activePartA.length, tone: "yellow" },
          { label: "Discharged (30d)", value: discharged30.length },
          { label: "Residents Readmitted", value: readmitResidents, tone: readmitResidents ? "red" : "green" },
        ],
        panels: [
          { type: "note", title: "How to read this", text: "Each row is a care episode (a stay) from PointClickCare. The Medicare A episode is the resident's Part A benefit window (admit → discharge). A resident with a discharged episode and a later admission is a readmission — the same signal the Rehospitalization reports derive from ADT, here at the stay level with payer context." },
          { type: "bars", title: "Episodes by Payer", data: groupBars(rows, (r) => r.payerType || "Other") },
          { type: "table", title: "Care Episodes (Stays)", rows: [...rows].sort((a, b) => String(b.admissionDate || "").localeCompare(String(a.admissionDate || ""))).slice(0, 200), empty: "No episodes of care in scope.", columns: [col("Resident", (r) => r.residentName, { strong: true }), facilityCol, col("Payer", (r) => r.payerType), col("Admitted", (r) => fmtDate(r.admissionDate)), col("Discharged", (r) => fmtDate(r.dischargeDate)), col("LOS", (r) => (r.lengthOfStay != null ? `${r.lengthOfStay}d` : "—"), { align: "right" }), col("Status", (r) => r.status)] },
        ],
      };
    },
  },
  HippsPdpm: {
    domain: "hipps",
    emptyHint: "Populates from the mdsAssessment.accepted webhook (pcc_hipps) — the finalized PDPM HIPPS + four CMG components on each accepted MDS. Add the MDS event group to the PointClickCare webhook subscription's eventGroupList; until an assessment is accepted it stays empty (degrades safely, never errors).",
    subtitle: "Finalized PDPM case-mix (HIPPS) from accepted MDS assessments — the 5-character HIPPS and its PT/OT · SLP · Nursing · NTA CMG components, plus the next assessment-window close (ARD).",
    build: (rows) => {
      const residents = new Set(rows.map((r) => r.residentId || r.residentName));
      const accepted = rows.filter((r) => /accept/i.test(String(r.assessmentStatus)));
      const nursingCmgs = new Set(rows.map((r) => r.nursingCmg).filter(Boolean));
      // Assessment windows closing within 7 days (next anticipated ARD is a FUTURE
      // date, so days-until = -daysSince).
      const closing = rows.filter((r) => { const d = r.nextAssessmentRefDate ? -daysSince(r.nextAssessmentRefDate) : null; return d != null && d >= 0 && d <= 7; });
      return {
        kpis: [
          { label: "Residents (HIPPS)", value: residents.size },
          { label: "Accepted", value: `${rate(accepted.length, rows.length) ?? 0}%`, tone: "green" },
          { label: "Distinct Nursing CMG", value: nursingCmgs.size },
          { label: "ARD Closing ≤7d", value: closing.length, tone: closing.length ? "yellow" : "green" },
        ],
        panels: [
          { type: "note", title: "How to read this", text: "Each row is the finalized PDPM HIPPS from an ACCEPTED MDS assessment — the moment the case-mix is locked for Medicare Part A billing. The five HIPPS characters map to the CMG components PT/OT, SLP, Nursing (which drives the nursing case-mix index) and NTA. 'Next ARD' is the anticipated assessment reference date — the window both a surveyor and the biller watch. This is the billed case-mix, not a risk-adjusted quality measure." },
          { type: "bars", title: "Residents by Nursing CMG", data: groupBars(rows, (r) => r.nursingCmg || "—", null, "sum", "#0f4c81") },
          { type: "bars", title: "Assessments by Type", data: groupBars(rows, (r) => r.assessmentType || "—") },
          { type: "table", title: "PDPM Case-Mix (HIPPS)", rows: [...rows].sort((a, b) => String(a.nextAssessmentRefDate || "9999").localeCompare(String(b.nextAssessmentRefDate || "9999"))).slice(0, 80), columns: [col("Resident", (r) => r.residentName, { strong: true }), facilityCol, col("HIPPS", (r) => r.hippsCode || "—", { strong: true }), col("PT/OT", (r) => r.ptOtCmg || "—", { align: "center" }), col("SLP", (r) => r.slpCmg || "—", { align: "center" }), col("Nursing", (r) => r.nursingCmg || "—", { align: "center" }), col("NTA", (r) => r.ntaCmg || "—", { align: "center" }), col("ARD", (r) => fmtDate(r.assessmentRefDate)), col("Next ARD", (r) => fmtDate(r.nextAssessmentRefDate)), col("Status", (r) => r.assessmentStatus || "—")] },
        ],
      };
    },
  },
  HighRiskFalls: {
    domain: "incidents",
    subtitle: "Falls — the highest-volume risk category.",
    build: (rows) => {
      const falls = rows.filter((r) => r.type === "fall");
      const inj = falls.filter((r) => r.injury);
      return {
        kpis: [
          { label: "Falls", value: falls.length, tone: falls.length > 10 ? "red" : "yellow" },
          { label: "With Injury", value: inj.length, tone: inj.length ? "red" : "green" },
          { label: "Injury Rate", value: `${rate(inj.length, falls.length) ?? 0}%`, tone: "yellow" },
        ],
        panels: [
          { type: "bars", title: "Falls by Location", data: groupBars(falls, (r) => r.location) },
          { type: "table", title: "Fall Incidents", rows: [...falls].sort((a, b) => String(b.date).localeCompare(String(a.date))), columns: [col("Date", (r) => fmtDate(r.date)), col("Resident", (r) => r.residentName, { strong: true }), facilityCol, col("Location", (r) => r.location), col("Severity", (r) => r.severity), col("Injury", (r) => yesNo(r.injury), { align: "center" })] },
        ],
      };
    },
  },
  HospitalTransferLog: {
    domain: "transfers",
    auxDomains: ["census"],
    subtitle: "Hospital transfers with the 30-day rehospitalization rate, unplanned %, and return-rate KPIs.",
    build: (rows, ctx) => {
      const unplanned = rows.filter((r) => r.kind === "unplanned");
      const unplanned30 = unplanned.filter((r) => { const d = daysSince(r.date); return d != null && d <= 30; });
      const returned = rows.filter((r) => r.returned);
      // 30-day rehospitalization rate = unplanned transfers (30d) ÷ current census.
      // Census arrives via ctx.aux; when unavailable the KPI shows "—".
      const censusTotal = (ctx?.aux?.census || []).reduce((a, c) => a + (c.occupied || 0), 0);
      const rehospRate = censusTotal ? fmtNum((unplanned30.length / censusTotal) * 100, 1) : null;
      // per-resident repeat-transfer drill-down
      const byRes = {};
      rows.forEach((r) => {
        const k = r.residentName;
        byRes[k] = byRes[k] || { residentName: r.residentName, facilityName: r.facilityName, count: 0, unplanned: 0 };
        byRes[k].count += 1;
        if (r.kind === "unplanned") byRes[k].unplanned += 1;
      });
      const repeat = Object.values(byRes).filter((x) => x.count > 1).sort((a, b) => b.count - a.count);
      return {
        kpis: [
          { label: "30d Rehosp Rate", value: rehospRate == null ? "—" : `${rehospRate}%`, sub: censusTotal ? `${unplanned30.length} / ${censusTotal} census` : "census unavailable", tone: "red" },
          { label: "Transfers", value: rows.length },
          { label: "Unplanned (30d)", value: unplanned30.length, tone: unplanned30.length ? "red" : "green" },
          { label: "% Unplanned", value: `${rate(unplanned.length, rows.length) ?? 0}%`, tone: "yellow" },
          { label: "Return Rate", value: `${rate(returned.length, rows.length) ?? 0}%`, tone: "green" },
        ],
        panels: [
          { type: "bars", title: "Transfers by Month", data: monthBars(rows, (r) => r.date, "#be123c") },
          { type: "bars", title: "By Type", data: groupBars(rows, (r) => r.kind) },
          { type: "table", title: "Repeat Transfers (2+)", rows: repeat, empty: "No residents with repeat transfers in scope.", columns: [col("Resident", (r) => r.residentName, { strong: true }), facilityCol, col("Transfers", (r) => r.count, { align: "right", strong: true }), col("Unplanned", (r) => r.unplanned, { align: "right" })] },
          { type: "table", title: "Transfer Log", rows: [...rows].sort((a, b) => String(b.date).localeCompare(String(a.date))), columns: [col("Date", (r) => fmtDate(r.date)), col("Resident", (r) => r.residentName, { strong: true }), facilityCol, col("Type", (r) => r.kind), col("Reason", (r) => r.reason, { wrap: true }), col("Returned", (r) => yesNo(r.returned), { align: "center" })] },
        ],
      };
    },
  },
  LevelOfCareTrend: {
    domain: "residents",
    subtitle: "Level-of-care distribution for AL and IL.",
    build: (rows) => {
      const alil = rows.filter((r) => r.levelOfCare !== "SNF");
      return {
        kpis: [{ label: "AL", value: alil.filter((r) => r.levelOfCare === "AL").length }, { label: "IL", value: alil.filter((r) => r.levelOfCare === "IL").length }],
        panels: [
          { type: "bars", title: "AL / IL by Facility", data: groupBars(alil, (r) => r.facilityName) },
          { type: "table", title: "AL / IL Residents", rows: alil, empty: "No AL/IL residents in scope.", columns: [col("Resident", (r) => r.name, { strong: true }), facilityCol, col("Level", (r) => r.levelOfCare), col("Admitted", (r) => fmtDate(r.admitDate))] },
        ],
      };
    },
  },
  MedicationSchedules: {
    domain: "residents",
    subtitle: "Medication counts per resident; 15+ flagged as high.",
    build: (rows) => ({
      kpis: [{ label: "Residents", value: rows.length }, { label: "High (15+ meds)", value: rows.filter((r) => r.medsCount >= 15).length, tone: "red" }],
      panels: [{ type: "table", title: "Medication Schedules", rows: [...rows].sort((a, b) => b.medsCount - a.medsCount), columns: [col("Resident", (r) => r.name, { strong: true }), facilityCol, col("Meds", (r) => r.medsCount, { align: "right", strong: true }), col("High Risk", (r) => yesNo(r.medsCount >= 15), { align: "center" })] }],
    }),
  },
  NewAdmitAudit: {
    domain: "residents",
    subtitle: "Recent admissions (last 14 days) — onboarding checklist.",
    build: (rows) => {
      const recent = rows.filter((r) => r.losDays <= 14).sort((a, b) => a.losDays - b.losDays);
      return {
        kpis: [{ label: "New Admits (14d)", value: recent.length }],
        panels: [{ type: "table", title: "New Admissions", rows: recent, empty: "No admissions in the last 14 days.", columns: [col("Admitted", (r) => fmtDate(r.admitDate)), col("Resident", (r) => r.name, { strong: true }), facilityCol, col("Level", (r) => r.levelOfCare), col("Payer", (r) => r.payer), col("Directive", (r) => r.advanceDirective || "—")] }],
      };
    },
  },
  PacketCompletion: {
    // LIVE PCC: documents domain (per-resident admission/consent document
    // metadata). PCC document records don't carry multi-signer counts, so
    // completion is measured as signed documents / total documents per resident.
    domain: "documents",
    emptyHint: "Needs admission/consent documents in PointClickCare for this facility's residents. The report is live — it fills as soon as PointClickCare returns document records.",
    subtitle: "Admission/consent document completeness from PointClickCare — signed vs. unsigned per resident.",
    build: (rows) => {
      // Group documents per resident to a completeness view.
      const byRes = {};
      rows.forEach((r) => {
        const k = r.residentId || r.residentName || "—";
        const e = (byRes[k] = byRes[k] || { residentName: r.residentName, facilityName: r.facilityName, total: 0, signed: 0, cats: new Set() });
        e.total += 1;
        if (r.signed) e.signed += 1;
        if (r.category) e.cats.add(r.category);
      });
      const resRows = Object.values(byRes).map((e) => ({ ...e, completionPct: rate(e.signed, e.total) ?? 0, categories: [...e.cats].join(", ") }));
      const signedCount = rows.filter((r) => r.signed).length;
      return {
        kpis: [
          { label: "Documents", value: rows.length },
          { label: "Signed", value: signedCount, tone: "green" },
          { label: "Unsigned", value: rows.length - signedCount, tone: "red" },
        ],
        panels: [
          { type: "bars", title: "Documents by Category", data: groupBars(rows, (r) => r.category) },
          { type: "table", title: "Completeness by Resident", rows: resRows.sort((a, b) => a.completionPct - b.completionPct), columns: [
            col("Resident", (r) => r.residentName, { strong: true }), facilityCol, col("Categories", (r) => r.categories || "—"),
            col("Signed", (r) => `${r.signed}/${r.total}`, { align: "right" }), col("Complete", (r) => pct(r.completionPct), { align: "right", strong: true }),
          ] },
        ],
      };
    },
  },
  PacketSignerDetail: {
    // LIVE PCC: documents domain — one row per document with its signed status.
    domain: "documents",
    subtitle: "Signature status per admission/consent document from PointClickCare.",
    build: (rows) => ({
      kpis: [
        { label: "Documents", value: rows.length },
        { label: "Signed", value: rows.filter((r) => r.signed).length, tone: "green" },
        { label: "Unsigned", value: rows.filter((r) => !r.signed).length, tone: "red" },
      ],
      panels: [{ type: "table", title: "Document Signature Detail", rows: [...rows].sort((a, b) => String(b.date || "").localeCompare(String(a.date || ""))), columns: [
        col("Resident", (r) => r.residentName, { strong: true }), facilityCol, col("Document", (r) => r.title || "—"), col("Category", (r) => r.category),
        col("Signed", (r) => yesNo(r.signed)), col("Status", (r) => r.status || "—"), col("Date", (r) => fmtDate(r.date)),
      ] }],
    }),
  },
  ProgressNotes: {
    domain: "progressNotes",
    subtitle: "Documentation volume per resident.",
    build: (rows) => ({
      kpis: [{ label: "Notes (30d)", value: rows.reduce((a, r) => a + r.notes30d, 0) }, { label: "Residents", value: rows.length }],
      panels: [
        { type: "bars", title: "Notes (30d) by Facility", data: groupBars(rows, (r) => r.facilityName, (r) => r.notes30d) },
        { type: "table", title: "Progress Notes", rows: [...rows].sort((a, b) => b.notes30d - a.notes30d).slice(0, 40), columns: [col("Resident", (r) => r.residentName, { strong: true }), facilityCol, col("7d", (r) => r.notes7d, { align: "right" }), col("30d", (r) => r.notes30d, { align: "right", strong: true }), col("Last Note", (r) => fmtDate(r.lastNote)) ] },
      ],
    }),
  },
  ProgressNotes1on1: {
    domain: "progressNotes",
    subtitle: "1-on-1 behavioral monitoring notes (last 30 days).",
    build: (rows) => {
      const active = rows.filter((r) => r.oneOnOne30d > 0).sort((a, b) => b.oneOnOne30d - a.oneOnOne30d);
      return {
        kpis: [{ label: "1-on-1 Notes (30d)", value: rows.reduce((a, r) => a + r.oneOnOne30d, 0) }, { label: "Residents on 1:1", value: active.length }],
        panels: [{ type: "table", title: "1-on-1 Monitoring", rows: active, empty: "No 1-on-1 notes in scope.", columns: [col("Resident", (r) => r.residentName, { strong: true }), facilityCol, col("1:1 Notes (30d)", (r) => r.oneOnOne30d, { align: "right", strong: true }), col("Last Note", (r) => fmtDate(r.lastNote))] }],
      };
    },
  },
  RecentMDS: {
    domain: "assessments",
    subtitle: "MDS assessments completed recently.",
    build: (rows) => {
      const done = rows.filter((r) => r.status === "Completed" && r.completedDate).sort((a, b) => String(b.completedDate).localeCompare(String(a.completedDate)));
      return {
        kpis: [{ label: "Completed", value: done.length, tone: "green" }],
        panels: [{ type: "table", title: "Recently Completed MDS", rows: done, empty: "No completed MDS in scope.", columns: [col("Completed", (r) => fmtDate(r.completedDate)), col("Resident", (r) => r.residentName, { strong: true }), facilityCol, col("Type", (r) => r.type), col("Assessor", (r) => r.assessor)] }],
      };
    },
  },
  RehospitalizationRisk: {
    domain: "residents",
    subtitle: "Residents stratified by rehospitalization risk.",
    build: (rows) => ({
      kpis: [
        { label: "High Risk", value: rows.filter((r) => r.rehospRisk === "High").length, tone: "red" },
        { label: "Moderate", value: rows.filter((r) => r.rehospRisk === "Moderate").length, tone: "yellow" },
        { label: "Low", value: rows.filter((r) => r.rehospRisk === "Low").length, tone: "green" },
      ],
      panels: [
        // ── WHAT THIS SCORE IS, AND THE ONE THING IT DOES NOT CONTAIN ────────────
        //
        // The score is the additive scorecard in pccIntegration.js (REHOSP_WEIGHTS):
        // hospital transfers and discharge→return cycles, high-risk chronic diagnoses,
        // polypharmacy, high-risk medication classes, a recent Significant Change, and
        // the early-admission window. There is no functional term in it.
        //
        // That is not an oversight, and it is why this is a note rather than a column.
        // The Priority Scan's composite adds functional risk SEPARATELY, alongside this
        // score (residents.mjs: clinical + ... + adlRisk), so folding function in here
        // would count the same signal twice on the list nurses actually work from.
        //
        // But a reader of THIS report never sees that composite. They see a tier called
        // "rehospitalization risk" and have no way to know function is not in it — so a
        // resident declining on ADLs with no transfers and no polypharmacy reads as Low.
        // That is a true number and a misleading screen, which is this product's
        // signature defect in its quietest form.
        //
        // ── AND THE FUNCTIONAL SIGNAL ITSELF IS HALF A SIGNAL ────────────────────
        //
        // The first version of this note said function "is tracked separately" and
        // stopped there, which overstates it in the direction that is hardest to catch.
        // public.pcc_adl is NOT empty -- 226 residents, all of them, checked in
        // production -- so the functional term computes and the screen shows numbers.
        // What fills that table is ADL answers recorded on an ASSESSMENT. What is
        // missing is CNA point-of-care charting: self-performance per activity, shift by
        // shift, which needs the pointOfCare.read scope and is not granted.
        //
        // A domain returning NOTHING announces itself. A domain returning the thin half
        // of itself looks exactly like one that works. functional-baseline.mjs is
        // explicit that the two are not interchangeable -- an assessment answer is an
        // interim baseline on the facility's own wording, usually a single shift -- so
        // the note has to name which half it had. See PENDING_SCOPES.POINT_OF_CARE_ADL.
        {
          type: "note",
          title: "What this score is built from",
          text:
            "Hospital transfers and discharge-and-return cycles, high-risk chronic diagnoses, polypharmacy, high-risk medication classes, a Significant Change assessment in the last 90 days, and the first 30 days of a Medicare A stay. Every score carries the specific reasons that produced it. FUNCTIONAL DECLINE IS NOT ONE OF THESE FACTORS — it is computed separately, and the Priority Scan's composite adds it alongside this score rather than inside it. Read that separate signal narrowly too: it comes from ADL answers recorded on assessments, because CNA point-of-care charting (self-performance per activity, shift by shift) needs a PointClickCare scope that is not granted and has no date. So a Low here means low on the factors above; it is not a statement that the resident's function is holding, and nothing on this screen has read the day-to-day ADL record.",
        },
        { type: "bars", title: "By Risk Tier", data: groupBars(rows, (r) => r.rehospRisk) },
        { type: "table", title: "High-Risk Residents", rows: rows.filter((r) => r.rehospRisk === "High"), empty: "No high-risk residents in scope.", columns: [col("Resident", (r) => r.name, { strong: true }), facilityCol, col("Level", (r) => r.levelOfCare), col("Meds", (r) => r.medsCount, { align: "right" }), col("Part A", (r) => (r.partADay != null ? `Day ${r.partADay}` : "—"), { align: "right" }), col("Readmit", (r) => (r.recentlyReadmitted ? "⚠ <30d" : "—"), { align: "center" }), col("Risk", (r) => riskPill(r.rehospRisk), { align: "center" })] },
      ],
    }),
  },
  RehospitalizationRate: {
    domain: "residents",
    subtitle: "30-day unplanned readmission rate and hospitalization burden, computed from PointClickCare ADT history.",
    build: (rows) => {
      const total = rows.length;
      const totalReadmit30 = rows.reduce((a, r) => a + (Number(r.readmission30dUnplanned) || 0), 0);
      const totalHosp = rows.reduce((a, r) => a + (Number(r.readmissionCount) || 0), 0);
      const affected = rows.filter((r) => (Number(r.readmission30dUnplanned) || 0) > 0);
      // Facility KPI: 30-day unplanned readmissions per 100 current residents.
      const ratePer100 = total ? fmtNum((totalReadmit30 / total) * 100, 1) : null;
      return {
        kpis: [
          { label: "30-Day Readmit Rate", value: ratePer100 == null ? "—" : `${ratePer100}%`, sub: `${totalReadmit30} readmits / ${total} residents`, tone: "red" },
          { label: "Residents Affected", value: affected.length, sub: "≥1 unplanned 30-day return", tone: "yellow" },
          { label: "Total Hospitalizations", value: totalHosp, sub: "all ADT hospital episodes" },
          { label: "Readmitted (Part A, 30d)", value: rows.filter((r) => r.recentlyReadmitted).length, sub: "episode-based (discharge → return)", tone: "red" },
          { label: "High Risk Now", value: rows.filter((r) => r.rehospRisk === "High").length, sub: "current risk tier", tone: "red" },
        ],
        panels: [
          { type: "bars", title: "Current Residents by Risk Tier", data: groupBars(rows, (r) => r.rehospRisk) },
          {
            type: "table",
            title: "Residents with a 30-Day Unplanned Readmission",
            rows: affected.slice().sort((a, b) => (Number(b.readmission30dUnplanned) || 0) - (Number(a.readmission30dUnplanned) || 0)),
            empty: "No 30-day unplanned readmissions in scope.",
            columns: [
              col("Resident", (r) => r.name, { strong: true }),
              facilityCol,
              col("Hospitalizations", (r) => r.readmissionCount ?? "—", { align: "right" }),
              col("30-Day Unplanned", (r) => r.readmission30dUnplanned ?? "—", { align: "right" }),
              col("Part A", (r) => (r.partADay != null ? `Day ${r.partADay}` : "—"), { align: "right" }),
              col("Risk", (r) => riskPill(r.rehospRisk), { align: "center" }),
            ],
          },
          { type: "note", title: "How this is computed", text: "A 30-day unplanned readmission is an unplanned hospital return within 30 days of the prior hospitalization, counted from PointClickCare ADT records as distinct episodes (out/return pairs de-duplicated). Rate = total such readmissions ÷ current residents × 100. Planned transfers (dialysis, scheduled procedures) are excluded where the ADT record marks them. Requires enriched residents (enrich=1)." },
        ],
      };
    },
  },
  ResidentLatestAssessment: {
    domain: "residents",
    subtitle: "Most recent assessment date per resident.",
    build: (rows) => ({
      kpis: [{ label: "Residents", value: rows.length }],
      panels: [{ type: "table", title: "Latest Assessment", rows: [...rows].sort((a, b) => String(b.latestAssessmentDate).localeCompare(String(a.latestAssessmentDate))).slice(0, 60), columns: [col("Resident", (r) => r.name, { strong: true }), facilityCol, col("Level", (r) => r.levelOfCare), col("Latest Assessment", (r) => fmtDate(r.latestAssessmentDate)), col("Room", (r) => r.room)] }],
    }),
  },
  UDAScheduleAudit: {
    domain: "assessments",
    subtitle: "User-defined assessment schedule audit — due and overdue.",
    build: (rows) => {
      const open = rows.filter((r) => r.status !== "Completed");
      return {
        kpis: [{ label: "Scheduled", value: rows.length }, { label: "Overdue", value: rows.filter((r) => r.status === "Overdue").length, tone: "red" }],
        panels: [{ type: "table", title: "UDA Schedule", rows: open.sort((a, b) => String(a.dueDate).localeCompare(String(b.dueDate))), empty: "Nothing scheduled in scope.", columns: [col("Due", (r) => fmtDate(r.dueDate)), col("Resident", (r) => r.residentName, { strong: true }), facilityCol, col("Type", (r) => r.type), col("Status", (r) => statusPill(r.status), { align: "center" })] }],
      };
    },
  },
  UnplannedHospitalDischarge: {
    domain: "transfers",
    subtitle: "Unplanned hospital discharges — summary.",
    build: (rows) => {
      const up = rows.filter((r) => r.kind === "unplanned");
      return {
        kpis: [{ label: "Unplanned", value: up.length, tone: "red" }, { label: "Rehospitalized", value: up.filter((r) => r.rehospitalized).length, tone: "red" }, { label: "Returned", value: up.filter((r) => r.returned).length, tone: "green" }],
        panels: [{ type: "bars", title: "By Reason", data: groupBars(up, (r) => r.reason) }],
      };
    },
  },
  UnplannedHospitalDischargeDetail: {
    domain: "transfers",
    subtitle: "Unplanned hospital discharges — line-item detail.",
    build: (rows) => {
      const up = rows.filter((r) => r.kind === "unplanned").sort((a, b) => String(b.date).localeCompare(String(a.date)));
      return {
        kpis: [{ label: "Unplanned", value: up.length, tone: "red" }],
        panels: [{ type: "table", title: "Unplanned Discharge Detail", rows: up, empty: "No unplanned discharges in scope.", columns: [col("Date", (r) => fmtDate(r.date)), col("Resident", (r) => r.residentName, { strong: true }), facilityCol, col("Reason", (r) => r.reason, { wrap: true }), col("Hosp LOS", (r) => `${r.los}d`, { align: "right" }), col("Returned", (r) => yesNo(r.returned), { align: "center" }), col("Rehosp", (r) => yesNo(r.rehospitalized), { align: "center" })] }],
      };
    },
  },

  // ── Nutrition / Observations ───────────────────────────────────────────────
  WeightLossNutrition: {
    domain: "observations",
    auxDomains: ["census"],
    subtitle: "Significant weight loss (F692) and nutrition signal — PointClickCare-flagged, per resident.",
    build: (rows, ctx) => {
      const weight = rows.filter((r) => /weight/i.test(r.type));
      const residentsWithWeight = new Set(weight.map((r) => r.residentName));
      // worst (most-negative) flagged reading per resident
      const worstByRes = {};
      weight.filter((r) => r.weightLossFlag).forEach((r) => {
        const k = r.residentName;
        if (!worstByRes[k] || (r.pctChange ?? 0) < (worstByRes[k].pctChange ?? 0)) worstByRes[k] = r;
      });
      const mostAffected = Object.values(worstByRes).sort((a, b) => (a.pctChange ?? 0) - (b.pctChange ?? 0));
      // Rate denominator = current census when available (the CMS QM basis),
      // otherwise fall back to residents with a weight reading in scope.
      const censusTotal = (ctx?.aux?.census || []).reduce((a, c) => a + (c.occupied || 0), 0);
      const denom = censusTotal || residentsWithWeight.size;
      return {
        kpis: [
          { label: censusTotal ? "Census" : "Residents (weights)", value: denom },
          { label: "Significant Loss", value: mostAffected.length, tone: mostAffected.length ? "red" : "green" },
          { label: "Sig. Loss Rate", value: `${rate(mostAffected.length, denom) ?? 0}%`, sub: censusTotal ? "of census" : "of weighed residents", tone: "yellow" },
        ],
        panels: [
          { type: "bars", title: "Flagged Weight Loss by Facility", data: groupBars(mostAffected, (r) => r.facilityName, null, "sum", "#be123c") },
          { type: "table", title: "Most-Affected Residents", rows: mostAffected, empty: "No PointClickCare-flagged significant weight loss in scope.", columns: [col("Resident", (r) => r.residentName, { strong: true }), facilityCol, col("% Change", (r) => (r.pctChange != null ? `${fmtNum(r.pctChange, 1)}%` : "—"), { align: "right", strong: true }), col("Last Weight", (r) => (r.value != null ? `${r.value} ${r.unit || ""}`.trim() : "—"), { align: "right" }), col("Date", (r) => fmtDate(r.date)), col("PointClickCare Warning", (r) => (r.warnings || []).join("; ") || "—", { wrap: true })] },
        ],
      };
    },
  },

  // ── Risk › Falls ─────────────────────────────────────────────────────────
  FallAnalysis: {
    domain: "incidents",
    subtitle: "Fall root-cause analysis — location, severity, care-plan follow-through.",
    build: (rows) => {
      const f = rows.filter((r) => r.type === "fall");
      const cp = f.filter((r) => r.carePlanUpdated);
      return {
        kpis: [{ label: "Falls", value: f.length }, { label: "Care Plan Updated", value: `${rate(cp.length, f.length) ?? 0}%`, tone: rate(cp.length, f.length) >= 90 ? "green" : "yellow" }, { label: "Witnessed", value: `${rate(f.filter((r) => r.witnessed).length, f.length) ?? 0}%` }],
        panels: [
          { type: "bars", title: "By Location", data: groupBars(f, (r) => r.location) },
          { type: "bars", title: "By Severity", data: groupBars(f, (r) => r.severity) },
        ],
      };
    },
  },
  FallIncidentList: {
    domain: "incidents",
    subtitle: "Every fall incident.",
    build: (rows) => {
      const f = rows.filter((r) => r.type === "fall").sort((a, b) => String(b.date).localeCompare(String(a.date)));
      return {
        kpis: [{ label: "Falls", value: f.length }],
        panels: [{ type: "table", title: "Fall Incidents", rows: f, empty: "No falls in scope.", columns: [col("Date", (r) => fmtDate(r.date)), col("Resident", (r) => r.residentName, { strong: true }), facilityCol, natureCol, col("Location", (r) => r.location), col("Severity", (r) => r.severity), col("Resp (min)", (r) => r.responseMinutes, { align: "right" }), col("Care Plan", (r) => yesNo(r.carePlanUpdated), { align: "center" })] }],
      };
    },
  },
  FallsDashboard: {
    domain: "incidents",
    subtitle: "Falls at a glance.",
    build: (rows) => {
      const f = rows.filter((r) => r.type === "fall");
      // Repeat fallers = distinct residents with 2+ falls in scope (computed from
      // the data — the incident feed doesn't carry a per-event `repeat` flag).
      const fallsByRes = {};
      f.forEach((r) => { const k = r.residentId || r.residentName; if (k) fallsByRes[k] = (fallsByRes[k] || 0) + 1; });
      const repeatFallers = Object.values(fallsByRes).filter((n) => n > 1).length;
      return {
        kpis: [{ label: "Falls", value: f.length, tone: "yellow" }, { label: "With Injury", value: f.filter((r) => r.injury).length, tone: "red" }, { label: "Repeat Fallers", value: repeatFallers, tone: "yellow" }, { label: "Avg Response", value: `${fmtNum(average(f.map((r) => r.responseMinutes)), 0)}m` }],
        panels: [
          { type: "bars", title: "Falls by Month", data: monthBars(f, (r) => r.date, "#d97706") },
          { type: "bars", title: "Falls by Location", data: groupBars(f, (r) => r.location) },
        ],
      };
    },
  },

  // ── Risk › Wounds ────────────────────────────────────────────────────────
  WoundAnalysis: {
    domain: "incidents",
    subtitle: "Pressure injuries by stage.",
    build: (rows) => {
      const w = rows.filter((r) => r.type === "wound");
      return {
        kpis: [{ label: "Wounds", value: w.length, tone: w.length ? "red" : "green" }, { label: "Care Plan Updated", value: `${rate(w.filter((r) => r.carePlanUpdated).length, w.length) ?? 0}%`, tone: "yellow" }],
        panels: [{ type: "bars", title: "By Stage", data: groupBars(w, (r) => r.woundStage || "Unspecified") }],
      };
    },
  },
  WoundIncidentList: {
    domain: "incidents",
    subtitle: "Every wound incident.",
    build: (rows) => {
      const w = rows.filter((r) => r.type === "wound").sort((a, b) => String(b.date).localeCompare(String(a.date)));
      return {
        kpis: [{ label: "Wounds", value: w.length }],
        panels: [{ type: "table", title: "Wound Incidents", rows: w, empty: "No wound incidents in scope.", columns: [col("Date", (r) => fmtDate(r.date)), col("Resident", (r) => r.residentName, { strong: true }), facilityCol, natureCol, col("Stage", (r) => r.woundStage || "—"), col("Care Plan", (r) => yesNo(r.carePlanUpdated), { align: "center" })] }],
      };
    },
  },

  // ── Risk › loose reports ───────────────────────────────────────────────────
  IncidentAllFacilities: {
    domain: "incidents",
    subtitle: "Portfolio incident volume across all facilities.",
    build: (rows) => ({
      kpis: [{ label: "Incidents", value: rows.length }, { label: "With Injury", value: rows.filter((r) => r.injury).length, tone: "red" }, { label: "Facilities", value: new Set(rows.map((r) => r.facilityCcn)).size }],
      panels: [
        { type: "bars", title: "Incidents by Facility", data: groupBars(rows, (r) => r.facilityName) },
        { type: "bars", title: "Incidents by Type/Nature", data: groupBars(rows, (r) => natureLabel(r)) },
      ],
    }),
  },
  DuplicateIncident: {
    domain: "incidents",
    subtitle: "Potential duplicate incident entries (same resident, date, and type).",
    build: (rows) => {
      const seen = {};
      rows.forEach((r) => { const k = `${r.residentName}|${r.date}|${r.type}`; (seen[k] = seen[k] || []).push(r); });
      const dups = Object.values(seen).filter((g) => g.length > 1).flat();
      return {
        kpis: [{ label: "Possible Duplicates", value: dups.length, tone: dups.length ? "red" : "green" }],
        panels: [{ type: "table", title: "Potential Duplicates", rows: dups, empty: "No duplicate incidents detected.", columns: [col("Date", (r) => fmtDate(r.date)), col("Resident", (r) => r.residentName, { strong: true }), facilityCol, natureCol, col("Incident ID", (r) => r.id)] }],
      };
    },
  },
  IncidentFacilityComparison: {
    domain: "incidents",
    subtitle: "Incident counts by type, per facility.",
    build: (rows) => {
      const byFac = {};
      rows.forEach((r) => {
        byFac[r.facilityName] = byFac[r.facilityName] || { facilityName: r.facilityName, fall: 0, wound: 0, elopement: 0, other: 0, total: 0 };
        byFac[r.facilityName][r.type] = (byFac[r.facilityName][r.type] || 0) + 1;
        byFac[r.facilityName].total += 1;
      });
      const table = Object.values(byFac).sort((a, b) => b.total - a.total);
      return {
        kpis: [{ label: "Incidents", value: rows.length }],
        panels: [
          { type: "bars", title: "Total Incidents by Facility", data: groupBars(rows, (r) => r.facilityName) },
          { type: "table", title: "By Facility & Type", rows: table, columns: [facilityCol, col("Falls", (r) => r.fall, { align: "right" }), col("Wounds", (r) => r.wound, { align: "right" }), col("Elopement", (r) => r.elopement, { align: "right" }), col("Other", (r) => r.other, { align: "right" }), col("Total", (r) => r.total, { align: "right", strong: true })] },
        ],
      };
    },
  },
  IncidentCarePlanAudit: {
    domain: "incidents",
    subtitle: "Incidents missing a care-plan update — the documentation gap.",
    build: (rows) => {
      const gap = rows.filter((r) => !r.carePlanUpdated);
      return {
        kpis: [{ label: "Incidents", value: rows.length }, { label: "Care Plan Updated", value: `${rate(rows.filter((r) => r.carePlanUpdated).length, rows.length) ?? 0}%`, tone: "yellow" }, { label: "Missing Update", value: gap.length, tone: gap.length ? "red" : "green" }],
        panels: [{ type: "table", title: "Missing Care-Plan Update", rows: gap, empty: "All incidents have a care-plan update.", columns: [col("Date", (r) => fmtDate(r.date)), col("Resident", (r) => r.residentName, { strong: true }), facilityCol, natureCol, col("Severity", (r) => r.severity)] }],
      };
    },
  },
  IncidentDetail: {
    domain: "incidents",
    subtitle: "Full incident detail.",
    build: (rows) => ({
      kpis: [{ label: "Incidents", value: rows.length }],
      panels: [{ type: "table", title: "Incident Detail", rows: [...rows].sort((a, b) => String(b.date).localeCompare(String(a.date))), columns: [col("Date", (r) => fmtDate(r.date)), col("Resident", (r) => r.residentName, { strong: true }), facilityCol, natureCol, col("Location", (r) => r.location), col("Severity", (r) => r.severity), col("Resp (min)", (r) => r.responseMinutes, { align: "right" }), col("Care Plan", (r) => yesNo(r.carePlanUpdated), { align: "center" })] }],
    }),
  },
  IncidentList: {
    domain: "incidents",
    subtitle: "All incidents.",
    build: (rows) => ({
      kpis: [{ label: "Incidents", value: rows.length }, { label: "Falls", value: rows.filter((r) => r.type === "fall").length }, { label: "Wounds", value: rows.filter((r) => r.type === "wound").length }],
      panels: [
        { type: "bars", title: "By Type/Nature", data: groupBars(rows, (r) => natureLabel(r)) },
        { type: "table", title: "Incident List", rows: [...rows].sort((a, b) => String(b.date).localeCompare(String(a.date))), columns: [col("Date", (r) => fmtDate(r.date)), col("Resident", (r) => r.residentName, { strong: true }), facilityCol, natureCol, col("Severity", (r) => r.severity), col("Injury", (r) => yesNo(r.injury), { align: "center" })] },
      ],
    }),
  },
  IncidentTrend: {
    domain: "incidents",
    subtitle: "Incident volume over the last 6 months.",
    build: (rows) => ({
      kpis: [{ label: "Incidents", value: rows.length }],
      panels: [
        { type: "bars", title: "All Incidents by Month", data: monthBars(rows, (r) => r.date) },
        { type: "bars", title: "Falls by Month", data: monthBars(rows.filter((r) => r.type === "fall"), (r) => r.date, "#d97706") },
      ],
    }),
  },
  InjuryDetail: {
    domain: "incidents",
    subtitle: "Incidents resulting in injury.",
    build: (rows) => {
      const inj = rows.filter((r) => r.injury).sort((a, b) => String(b.date).localeCompare(String(a.date)));
      return {
        kpis: [{ label: "Injuries", value: inj.length, tone: "red" }, { label: "Major", value: inj.filter((r) => r.severity === "Major").length, tone: "red" }],
        panels: [{ type: "table", title: "Injury Detail", rows: inj, empty: "No injuries in scope.", columns: [col("Date", (r) => fmtDate(r.date)), col("Resident", (r) => r.residentName, { strong: true }), facilityCol, natureCol, col("Severity", (r) => r.severity), col("Location", (r) => r.location)] }],
      };
    },
  },
  ResidentRepeatIncident: {
    domain: "incidents",
    subtitle: "Residents with more than one incident — recurrence signal.",
    build: (rows) => {
      const byRes = {};
      rows.forEach((r) => { byRes[r.residentName] = byRes[r.residentName] || { residentName: r.residentName, facilityName: r.facilityName, count: 0, falls: 0 }; byRes[r.residentName].count += 1; if (r.type === "fall") byRes[r.residentName].falls += 1; });
      const repeat = Object.values(byRes).filter((x) => x.count > 1).sort((a, b) => b.count - a.count);
      return {
        kpis: [{ label: "Repeat Residents", value: repeat.length, tone: repeat.length ? "red" : "green" }],
        panels: [{ type: "table", title: "Repeat Incident Residents", rows: repeat, empty: "No residents with repeat incidents.", columns: [col("Resident", (r) => r.residentName, { strong: true }), facilityCol, col("Incidents", (r) => r.count, { align: "right", strong: true }), col("Falls", (r) => r.falls, { align: "right" })] }],
      };
    },
  },
  ResponseTimeSummary: {
    domain: "incidents",
    subtitle: "Staff response time to incidents.",
    build: (rows) => {
      const times = rows.map((r) => r.responseMinutes).filter((n) => n != null);
      const buckets = [
        { label: "0–5 min", value: times.filter((t) => t <= 5).length, color: "#059669" },
        { label: "6–15 min", value: times.filter((t) => t > 5 && t <= 15).length, color: "#65a30d" },
        { label: "16–30 min", value: times.filter((t) => t > 15 && t <= 30).length, color: "#d97706" },
        { label: "31+ min", value: times.filter((t) => t > 30).length, color: "#dc2626" },
      ];
      return {
        kpis: [{ label: "Avg Response", value: `${fmtNum(average(times), 1)}m`, tone: average(times) <= 10 ? "green" : "yellow" }, { label: "Median", value: `${fmtNum(median(times), 1)}m` }, { label: "Incidents", value: rows.length }],
        panels: [
          { type: "bars", title: "Response Time Distribution", data: buckets },
          { type: "bars", title: "Avg Response by Type (min)", data: groupBars(rows, (r) => r.type, (r) => r.responseMinutes, "avg") },
        ],
      };
    },
  },

  // ── Sys ─────────────────────────────────────────────────────────────────────
  AvailableReports: {
    domain: "availableReports",
    subtitle: "Reports available in the platform.",
    build: (rows) => ({
      kpis: [{ label: "Reports", value: rows.length }, { label: "Live", value: rows.filter((r) => r.status === "Live").length, tone: "green" }],
      panels: [{ type: "table", title: "Available Reports", rows, columns: [col("Report", (r) => r.report, { strong: true }), col("Category", (r) => r.category), col("Owner", (r) => r.owner), col("Status", (r) => statusPill(r.status), { align: "center" })] }],
    }),
  },
  ClientSoftwareMapping: {
    domain: "subscriptions",
    subtitle: "EHR / source software by facility.",
    build: (rows) => ({
      kpis: [{ label: "Facilities", value: rows.length }],
      panels: [{ type: "table", title: "Software Mapping", rows, columns: [facilityCol, col("CCN", (r) => r.facilityCcn), col("EHR / Source", (r) => r.ehr, { strong: true }), col("Modules", (r) => r.modules, { wrap: true })] }],
    }),
  },
  ClientSubscriptionList: {
    domain: "subscriptions",
    subtitle: "Subscription status and renewals.",
    build: (rows) => ({
      kpis: [{ label: "Facilities", value: rows.length }, { label: "Active", value: rows.filter((r) => r.status === "Active").length, tone: "green" }, { label: "Seats", value: rows.reduce((a, r) => a + r.seats, 0) }],
      panels: [{ type: "table", title: "Subscriptions", rows, columns: [facilityCol, col("Modules", (r) => r.modules, { wrap: true }), col("Seats", (r) => r.seats, { align: "right" }), col("Renewal", (r) => fmtDate(r.renewal)), col("Status", (r) => statusPill(r.status), { align: "center" })] }],
    }),
  },
  JobsRunHistory: {
    domain: "sysJobs",
    subtitle: "Background data-pipeline job runs.",
    build: (rows) => ({
      kpis: [{ label: "Jobs", value: rows.length }, { label: "Success", value: `${rate(rows.filter((r) => r.status === "Success").length, rows.length) ?? 0}%`, tone: "green" }, { label: "Failed", value: rows.filter((r) => r.status === "Failed").length, tone: "red" }],
      panels: [{ type: "table", title: "Jobs Run History", rows, columns: [col("Job", (r) => r.job, { strong: true }), col("Last Run", (r) => r.lastRun), col("Duration", (r) => `${r.durationSec}s`, { align: "right" }), col("Records", (r) => r.records, { align: "right" }), col("Status", (r) => statusPill(r.status), { align: "center" })] }],
    }),
  },
  CensusLoadLog: {
    domain: "censusLoadLog",
    subtitle: "Daily census load from the source EHR.",
    build: (rows) => ({
      kpis: [{ label: "Loads", value: rows.length }, { label: "Records Loaded", value: rows.reduce((a, r) => a + r.recordsLoaded, 0) }],
      panels: [{ type: "table", title: "Census Load Log", rows, columns: [col("Load Date", (r) => fmtDate(r.loadDate)), facilityCol, col("Records", (r) => r.recordsLoaded, { align: "right", strong: true }), col("Source", (r) => r.source), col("Duration", (r) => `${r.durationSec}s`, { align: "right" }), col("Status", (r) => statusPill(r.status), { align: "center" })] }],
    }),
  },
  RolePermission: {
    domain: "rolePermissions",
    subtitle: "What each role can see and do.",
    build: (rows) => ({
      kpis: [{ label: "Roles", value: rows.length }],
      panels: [{ type: "table", title: "Role Permissions", rows, columns: [col("Role", (r) => r.role, { strong: true }), col("Reports", (r) => r.reports, { wrap: true }), col("Create", (r) => r.create, { align: "center" }), col("Admin", (r) => r.admin, { align: "center" }), col("Export", (r) => r.export, { align: "center" })] }],
    }),
  },

  // ── Admin ─────────────────────────────────────────────────────────────────
  UserAccountAudit: {
    domain: "accountAudit",
    subtitle: "Account access and security events.",
    build: (rows) => ({
      kpis: [{ label: "Events", value: rows.length }, { label: "Denied", value: rows.filter((r) => r.result === "Denied").length, tone: "red" }],
      panels: [{ type: "table", title: "Account Audit", rows, columns: [col("Date", (r) => r.date), col("User", (r) => r.user, { strong: true }), col("Action", (r) => r.action), col("IP", (r) => r.ip), col("Result", (r) => statusPill(r.result), { align: "center" })] }],
    }),
  },
  UserAccountChangeLog: {
    domain: "accountChanges",
    subtitle: "Changes to user accounts.",
    build: (rows) => ({
      kpis: [{ label: "Changes", value: rows.length }],
      panels: [{ type: "table", title: "Account Change Log", rows, columns: [col("Date", (r) => fmtDate(r.date)), col("User", (r) => r.user, { strong: true }), col("Field", (r) => r.field), col("From", (r) => r.oldValue), col("To", (r) => r.newValue), col("By", (r) => r.changedBy)] }],
    }),
  },
  ReportUsageDetail: {
    domain: "reportUsage",
    subtitle: "Report run detail over the last 30 days.",
    build: (rows) => ({
      kpis: [{ label: "Total Runs (30d)", value: rows.reduce((a, r) => a + r.runs30d, 0) }, { label: "Reports", value: rows.length }],
      panels: [{ type: "table", title: "Report Usage Detail", rows: [...rows].sort((a, b) => b.runs30d - a.runs30d), columns: [col("Report", (r) => r.report, { strong: true }), col("Runs (30d)", (r) => r.runs30d, { align: "right", strong: true }), col("Unique Users", (r) => r.uniqueUsers, { align: "right" }), col("Avg ms", (r) => r.avgMs, { align: "right" }), col("Last Run", (r) => fmtDate(r.lastRun))] }],
    }),
  },
  ReportUsageSummary: {
    domain: "reportUsage",
    subtitle: "Report runs ranked by volume.",
    build: (rows) => ({
      kpis: [{ label: "Total Runs (30d)", value: rows.reduce((a, r) => a + r.runs30d, 0) }],
      panels: [
        { type: "bars", title: "Runs by Report (30d)", data: groupBars(rows, (r) => r.report, (r) => r.runs30d) },
        { type: "table", title: "Usage Summary", rows: [...rows].sort((a, b) => b.runs30d - a.runs30d), columns: [col("Report", (r) => r.report, { strong: true }), col("Runs (30d)", (r) => r.runs30d, { align: "right", strong: true }), col("Last Run By", (r) => r.lastRunBy)] },
      ],
    }),
  },
  UserList: {
    domain: "sysUsers",
    subtitle: "Platform users and access.",
    build: (rows) => ({
      kpis: [{ label: "Users", value: rows.length }, { label: "Active", value: rows.filter((r) => r.status === "Active").length, tone: "green" }, { label: "MFA Enabled", value: `${rate(rows.filter((r) => r.mfa).length, rows.length) ?? 0}%`, tone: "yellow" }],
      panels: [{ type: "table", title: "Users", rows, columns: [col("Name", (r) => r.name, { strong: true }), col("Email", (r) => r.email), col("Role", (r) => r.role), col("Facilities", (r) => r.facilities), col("MFA", (r) => yesNo(r.mfa), { align: "center" }), col("Status", (r) => statusPill(r.status), { align: "center" })] }],
    }),
  },
};
